import { Component, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { BehaviorSubject, first, forkJoin } from 'rxjs';

import { SearchService, BreakpointService, BreadcrumbService, Preferences, KeyboardShortcutsService } from '@library/core/services';
import { ShipmentService } from '@library/modules/shipment';
import { TemplateService } from '../template.service';

import { SharedKeys, SharedKeysEnum, SharedKeysMap } from '@library/common/models';
import { Template } from '../template';
import { UseraccessService } from '@library/modules/user';

@Component( {
   selector: 'lib-template-manage',
   templateUrl: './template-manage.component.html',
   styleUrls: [ './template-manage.component.scss' ],
} )
export class TemplateManageComponent implements OnInit, OnDestroy {

   shared_key: SharedKeys | '' = '';
   SharedKeys = SharedKeys;
   SharedKeysMap = SharedKeysMap;
   SharedKeysEnum = SharedKeysEnum;

   _deleting: Template;
   _creating: string;

   _templates: BehaviorSubject<Template[]> = new BehaviorSubject( null );
   dataSource: MatTableDataSource<Template> = new MatTableDataSource();

   templateFilterValue: string;
   isShipment: boolean = false;
   isQuoting: boolean = false;

   @ViewChild(MatSort) sort: MatSort;
   @ViewChild(MatPaginator) paginator: MatPaginator;

   @ViewChild('deleteConfirm') deleteConfirm: ElementRef;

   get pageSize() { return this.prefs.getPageSize( 'templates' ); }
   set pageSize( value ) { this.prefs.setPageSize( 'templates', value ); }

   snackbarRef: MatSnackBarRef<SimpleSnackBar>;

   constructor(
      private ks: KeyboardShortcutsService,
      private search: SearchService,
      private breadcrumbs: BreadcrumbService,
      public breakpoints: BreakpointService,
      public prefs: Preferences,
      public router: Router,
      public shipment: ShipmentService,
      public snackbar: MatSnackBar,
      public templateService: TemplateService,
      private access: UseraccessService,
   ) {
      this.search.showSearch = true;
      this.search.params.get( 'text' ).valueChanges.subscribe( change => this.dataSource.filter = change );

      this.load();
   }

   ngOnInit() {
      this.ks.register$({
         description: 'New Template',
         key: 'N',
         keyDisplay: 'Shift + N',
         icon: 'add',
      }).subscribe( _ => this.router.navigate([ '/template/new' ]));

      this.breadcrumbs.newCrumb({ label: 'Templates', path: '/template' });
   }

   ngOnDestroy() {
      this.ks.remove( 'N' );
   }

   load() {
      this.templateService.getTemplates().subscribe( templates => {
         forkJoin([
            this.access.hasAccess('quoting').pipe(first()),
            this.access.hasAccess('booking').pipe(first()),
         ]).subscribe( ( [ q, b]) => {
            this.isShipment = b;
            this.isQuoting = q;
            
            this._templates.next( templates );
            this.dataSource.data = templates;

            setTimeout( _ => {
               this.dataSource.paginator = this.paginator;
               this.dataSource.sort = this.sort;
            }, 1 );
         });

      });
   }

   createShipment( id: string ) {
      this._creating = 'shipment' + id;
      this.templateService.getTemplate( id ).subscribe( t => {
         if ( t ) { this.shipment.shipment = t.data; }
         this.router.navigate([ '/create/shipment/new' ]);
      });
   }

   createQuotation( id: string ) {
      this._creating = 'quote' + id;
      this.templateService.getTemplate( id ).subscribe( t => {
         if ( t ) { this.shipment.shipment = t.data; }
         this.router.navigate([ '/create/quotation/new' ]);
      });
   }

   refresh() {
      this._templates.subscribe( t => {
         this.dataSource.data = t;
         this.filter_shared_key();
      });
   }

   toggle_shared_key( status: SharedKeys ): void {
      this.shared_key = ( this.shared_key === status ) ? '' : status;
      this.filter_shared_key();
   }

   filter_shared_key() {
      this._templates.subscribe( list => {
         this.dataSource.data = list;
         if ( this.shared_key ) {
            this.dataSource.data = this.dataSource.data
               .filter( r => this.shared_key === SharedKeysMap( r ));
         }
      });
   }

   get deleting() { return this._deleting; }
   set deleting( value: Template ) {
      this._deleting = value;
      if ( value == null ) {
         this.refresh();

         return;
      }
      this.dataSource.data = this.dataSource.data.filter( t => t.id === value.id );
      setTimeout( _ => this.deleteConfirm.nativeElement.scrollIntoView() );
   }

   delete( template: Template ) {
      if ( this.snackbarRef ) {
         this.snackbarRef.dismiss();
      }

      this.deleting = null;

      this.dataSource.data = this.dataSource.data.filter( t => t.id !== template.id );

      this.snackbarRef = this.snackbar.open( template.name + ' -- Deleted', 'Undo', {
         duration: 5000, verticalPosition: 'bottom'
      });

      this.snackbarRef.afterDismissed().subscribe( dismiss => {
         if ( dismiss.dismissedByAction ) {
            this.load();
         } else {
            this.templateService.deleteTemplate( template.id ).subscribe( _ => {
               this.load();
            }, error => {
               console.log( 'delete template error', error );
            });
         }
      });
   }
}
